import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { getComments, addComment } from '@/features/blog/services/blogApi';
import { useAuthStore } from '@/shared/stores/authStore';
import type { Comment } from '@/shared/types/api';
import Skeleton from '@/shared/components/Skeleton';

interface CommentFormProps {
  postId: number;
  parentId?: number;
  onCancel?: () => void;
  onSuccess?: () => void;
}

function CommentForm({ postId, parentId, onCancel, onSuccess }: CommentFormProps) {
  const queryClient = useQueryClient();
  const { isAuthenticated, username } = useAuthStore();
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [body, setBody] = useState('');

  const mutation = useMutation({
    mutationFn: (data: { author_name?: string; author_email?: string; body: string; parent_id?: number }) =>
      addComment(postId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postId] });
      setAuthorName('');
      setAuthorEmail('');
      setBody('');
      toast.success('Your comment has been posted.');
      onSuccess?.();
    },
    onError: () => {
      toast.error('Failed to submit comment.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;

    const payload: { author_name?: string; author_email?: string; body: string; parent_id?: number } = {
      body: body.trim(),
    };

    if (parentId) {
      payload.parent_id = parentId;
    }

    if (!isAuthenticated) {
      if (!authorName.trim()) return;
      payload.author_name = authorName.trim();
      payload.author_email = authorEmail.trim();
    }

    mutation.mutate(payload);
  };

  return (
    <div>
      {isAuthenticated && (
        <p className="text-sm text-muted mb-3">
          Commenting as <span className="font-bold text-accent">{username}</span>
        </p>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        {!isAuthenticated && (
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-bold mb-1">Name *
                <input
                  type="text"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  required
                  className="w-full px-2 py-1.5 border border-border text-sm font-mono focus:outline-none focus:border-accent"
                />
              </label>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-bold mb-1">Email
                <input
                  type="email"
                  value={authorEmail}
                  onChange={(e) => setAuthorEmail(e.target.value)}
                  className="w-full px-2 py-1.5 border border-border text-sm font-mono focus:outline-none focus:border-accent"
                />
              </label>
            </div>
          </div>
        )}
        <div>
          <label className="block text-xs font-bold mb-1">
            {parentId ? 'Reply *' : 'Comment *'}
          </label>
          <textarea
            rows={parentId ? 3 : 4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            className="w-full px-2 py-1.5 border border-border text-sm font-mono focus:outline-none focus:border-accent resize-vertical"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn-terminal disabled:opacity-50"
          >
            {mutation.isPending ? 'Submitting...' : 'Submit'}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-muted hover:text-accent"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

interface CommentItemProps {
  comment: Comment;
  postId: number;
}

function CommentItem({ comment, postId }: CommentItemProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);

  const approvedReplies = comment.replies?.filter((r) => r.is_approved) ?? [];

  return (
    <div>
      <div className="text-xs text-muted mb-1">
        <span className="font-bold text-accent">{comment.author_name}</span>
        {' '}on{' '}
        <time dateTime={comment.created_at}>
          {format(new Date(comment.created_at), 'MMMM d, yyyy')}
        </time>
      </div>
      <p className="text-sm leading-relaxed whitespace-pre-wrap m-0">{comment.body}</p>
      <button
        onClick={() => setShowReplyForm(!showReplyForm)}
        className="text-xs text-muted hover:text-accent mt-1 cursor-pointer"
      >
        {showReplyForm ? 'Cancel reply' : 'Reply'}
      </button>

      {showReplyForm && (
        <div className="mt-3 ml-6">
          <CommentForm
            postId={postId}
            parentId={comment.id}
            onCancel={() => setShowReplyForm(false)}
            onSuccess={() => setShowReplyForm(false)}
          />
        </div>
      )}

      {approvedReplies.length > 0 && (
        <div className="ml-6 mt-3 space-y-4">
          {approvedReplies.map((reply) => (
            <CommentItem key={reply.id} comment={reply} postId={postId} />
          ))}
        </div>
      )}
    </div>
  );
}

interface CommentSectionProps {
  postId: number;
}

export default function CommentSection({ postId }: CommentSectionProps) {
  const { data: comments, isLoading } = useQuery<Comment[]>({
    queryKey: ['comments', postId],
    queryFn: () => getComments(postId),
  });

  const topLevelComments = comments?.filter((c) => c.is_approved && !c.parent_id) ?? [];

  return (
    <section id="comments" className="mt-8 pb-8">
      <hr />
      <h3 className="text-base font-bold mt-6 mb-4">
        Comments{topLevelComments.length > 0 ? ` (${topLevelComments.length})` : ''}
      </h3>

      {isLoading && (
        <div className="space-y-4 mb-8">
          {[1, 2].map((i) => (
            <div key={i}>
              <Skeleton className="h-3 w-40 mb-2" />
              <Skeleton className="h-4 w-full mb-1" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      )}

      {topLevelComments.length > 0 && (
        <div className="space-y-4 mb-8">
          {topLevelComments.map((comment) => (
            <CommentItem key={comment.id} comment={comment} postId={postId} />
          ))}
        </div>
      )}

      {topLevelComments.length === 0 && !isLoading && (
        <p className="text-muted text-sm mb-6">No comments yet.</p>
      )}

      <h4 className="text-sm font-bold mb-3">Leave a Comment</h4>

      <CommentForm postId={postId} />
    </section>
  );
}
